import React, { useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../../../config/googleMaps";
import PickupLocationField from "./PickupLocationField";

const libraries = ["places"];

const RentalPickupLocations = ({
  locations,
  onChange,
  fieldError,
  markTouched,
}) => {
  const rows = Array.isArray(locations) && locations.length ? locations : [];
  const rowRefs = useRef({});

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const updateRows = (nextRows) => {
    onChange(nextRows);
  };

  const setPrimary = (index) => {
    updateRows(
      rows.map((row, i) => ({
        ...row,
        is_primary: i === index,
      }))
    );
  };

  const updateRow = (index, patch) => {
    updateRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    const nextIndex = rows.length;
    updateRows([
      ...rows,
      {
        id: null,
        name: "",
        latitude: "",
        longitude: "",
        place_id: null,
        is_primary: false,
      },
    ]);
    window.setTimeout(() => {
      const el = rowRefs.current[nextIndex];
      const input = el?.querySelector("input");
      input?.focus();
    }, 50);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    const next = rows.filter((_, i) => i !== index);
    if (!next.some((r) => r.is_primary)) {
      next[0] = { ...next[0], is_primary: true };
    }
    updateRows(next);
  };

  const listError = fieldError?.("pickup_locations");

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Pickup locations <span className="text-red-500">*</span>
          </label>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Add every place customers can collect this item. Mark one as primary for listings.
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="shrink-0 text-[11px] font-semibold text-primary hover:underline"
        >
          + Add location
        </button>
      </div>

      {loadError ? (
        <div className="text-xs font-medium text-red-600">
          Could not load Google Maps. Check your API key and try again.
        </div>
      ) : null}

      {listError ? (
        <div className="text-xs font-medium text-red-600">{listError}</div>
      ) : null}

      <div className="space-y-3">
        {rows.map((row, index) => {
          const instanceId = `${row.id ?? "new"}-${index}`;
          const nameError = fieldError?.(`pickup_locations.${index}.name`);
          const latError = fieldError?.(`pickup_locations.${index}.latitude`);
          const lngError = fieldError?.(`pickup_locations.${index}.longitude`);
          const rowError = nameError || latError || lngError;

          return (
            <div
              key={instanceId}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              className="rounded-lg border border-gray-200 bg-white p-3 space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Location {index + 1}
                </span>
                {rows.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="text-[11px] font-semibold text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                ) : null}
              </div>

              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                <input
                  type="radio"
                  name="rental-pickup-primary"
                  checked={Boolean(row.is_primary)}
                  onChange={() => setPrimary(index)}
                  className="text-primary focus:ring-primary"
                />
                Primary location (shown on listing)
              </label>

              {!isLoaded ? (
                <div className="text-sm text-gray-500 py-2">Loading location search…</div>
              ) : (
                <PickupLocationField
                  instanceId={instanceId}
                  value={row.name}
                  mapsReady={isLoaded}
                  hasError={Boolean(rowError)}
                  placeholder="Start typing an address, then pick from suggestions"
                  onBlur={() => markTouched?.("pickup_locations")}
                  onChange={(patch) => {
                    markTouched?.("pickup_locations");
                    updateRow(index, patch);
                  }}
                />
              )}

              {nameError ? (
                <div className="text-xs font-medium text-red-600">{nameError}</div>
              ) : null}
              {latError ? (
                <div className="text-xs font-medium text-red-600">{latError}</div>
              ) : null}
              {lngError ? (
                <div className="text-xs font-medium text-red-600">{lngError}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RentalPickupLocations;
